/*
 * Copyright (c) Nexign, JSC, 2019-2024.
 */
/**
 * The wrapper for all corporate pages
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Maxim Fedorov
 * @author Anton Panasenko
 * @author Julia Ignatieva
 * @author Ilya Baranchikov
 * @version 1.30.0
 * @since 1.0.0
 */

import { cloneElement, ReactChild, ReactElement, useEffect } from 'react';

import { IHideBannerPayload } from '@nexign/scc-ui-kit/lib/components/Banner/types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { Footer } from '../../components/common';
import HelpTips from '../../components/Header/components/HelpTips';
import { setHelpFlagAction } from '../../components/Header/headerActions';
import { makeSelectorHelpFlag, makeSelectorHelpTipText } from '../../components/Header/headerSelectors';
import { pushGTMAnalytic } from '../../modules/analytics/GoogleTagManager/GTMActions';
import { IGTMPushAnalyticPayload } from '../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import appHistory from '../../modules/app/appHistory';
import { hideAlternativeBanner, hideBanner, loadBanner } from '../../modules/banner/bannerActions';
import { makeSelectorBannersList, makeSelectorBannerTotalCount } from '../../modules/banner/bannerSelectors';
import { IBannerItem } from '../../modules/banner/bannerTypes';
import Banner from '../../modules/banner/components/Banner';
import { makeSelectBreadCrumbsData } from '../../modules/breadCrumbs/breadCrumbsSelectors';
import { IBreadCrumb } from '../../modules/breadCrumbs/breadCrumbsTypes';
import { BreadCrumbs } from '../../modules/breadCrumbs/components/BreadCrumbs';
import { makeSelectTranslationCommon } from '../../modules/internationalization/intSelectors';
import { ICommonTranslation, ICorporateTranslation } from '../../modules/internationalization/types';
import ICommonTranslationContextHelp from '../../modules/internationalization/types/common/contextHelp';
import { IRootReducer } from '../../reducers/rootReducer';
import { IRoutePath, RoutePath } from '../../routes/constants/routesConstants';
import { makeSelectLoggedIn } from '../PageAuth/selectors/authSelectors';
import {
    CORPORATE_PAGE_FOOTER_PORTAL_ID,
    CORPORATE_PAGE_PORTAL_ID,
    pagesWithBanner,
    pageWithoutCorporateHelp,
} from './constants';
import { CorporatePageContainerStyled, CorporatePageStyled } from './styled/CorporatePageStyled';

export interface ICorporatePage {
    breadCrumbs?: IBreadCrumb[];
    children: ReactChild;
    render: () => ReactElement;
}

export interface IConnectedProps {
    banners: IBannerItem[];
    bannerTotalCount: number;
    breadCrumbsData: IBreadCrumb[];
    helpFlag: boolean;
    helpTipText: string;
    loggedIn: boolean;
    translation: ICommonTranslation & ICorporateTranslation;
}

export interface IDispatchProps {
    hideBanner: (payload: IHideBannerPayload) => void;
    hideAlternativeBanner: (payload: IHideBannerPayload) => void;
    loadBanner: () => void;
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
    setHelpFlag: (payload: boolean) => void;
}

export type TCorporatePage = ICorporatePage &
    IConnectedProps &
    IDispatchProps &
    Partial<Pick<RouteComponentProps, 'match'>>;

export const CorporatePage = (props: TCorporatePage) => {
    const onCloseHelpTips = () => {
        props.setHelpFlag(false);
    };

    // load banners
    useEffect(() => {
        props.loadBanner();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const className = classNames({
        'corporate-page_dashboard': appHistory.location.pathname === RoutePath.dashboard,
        'corporate-page_products': appHistory.location.pathname.startsWith(RoutePath.products),
    });

    /**
     * Commonly text for help tip is defined in common translation
     * To change the text dynamically, use action to override default text
     * @see setHelpTipTextAction
     */
    const helpTipText = props.helpTipText || getContextHelpText(props.translation.contextHelp, location.pathname);

    return (
        <CorporatePageContainerStyled className={className}>
            {pagesWithBanner.includes(props.match?.path || appHistory.location.pathname) && (
                <Banner
                    banners={props.banners}
                    hideAlternativeBanner={props.hideAlternativeBanner}
                    hideBanner={props.hideBanner}
                />
            )}
            {props.loggedIn && props.helpFlag && helpTipText && (
                <HelpTips contextHelpText={helpTipText} onCloseHelpTips={onCloseHelpTips} />
            )}
            <div id={CORPORATE_PAGE_PORTAL_ID} />

            {cloneElement(props.render(), {}, [
                <BreadCrumbs
                    data={props.breadCrumbsData}
                    key="breadCrumbs"
                    pushGtmAnalytic={props.pushGtmAnalytic}
                    translation={props.translation}
                />,
                props.children,
                <Footer key="footer" {...props.translation} />,
            ])}
            <div id={CORPORATE_PAGE_FOOTER_PORTAL_ID} style={{ position: 'sticky', bottom: 33 }} />
        </CorporatePageContainerStyled>
    );
};

const isRoutePathMatches = (routePath: string, location: string): boolean => {
    const template = routePath.replace(/:[^/]*/, '(.*)');
    return !!location.match(template);
};

/**
 * Get contextHelp text according to location.
 */
export const getContextHelpText = (translation: ICommonTranslationContextHelp, location: string): string => {
    let result = '';
    if (!pageWithoutCorporateHelp.filter((route) => location.match(route)).length) {
        Object.keys(RoutePath)
            .filter((routePath) => isRoutePathMatches(RoutePath[routePath as keyof IRoutePath], location))
            .forEach((routePath) => {
                const helpText = translation[routePath as keyof ICommonTranslationContextHelp];
                if (helpText) {
                    result = helpText;
                }
            });
    }
    return result;
};

CorporatePage.defaultProps = {
    render: () => <CorporatePageStyled />,
};

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    banners: makeSelectorBannersList(),
    bannerTotalCount: makeSelectorBannerTotalCount(),
    breadCrumbsData: makeSelectBreadCrumbsData(),
    helpFlag: makeSelectorHelpFlag(),
    helpTipText: makeSelectorHelpTipText(),
    loggedIn: makeSelectLoggedIn(),
    translation: makeSelectTranslationCommon(),
});

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    hideBanner: (payload: IHideBannerPayload) => dispatch(hideBanner(payload)),
    hideAlternativeBanner: (payload: IHideBannerPayload) => dispatch(hideAlternativeBanner({ id: payload.id })),
    loadBanner: () => dispatch(loadBanner()),
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => dispatch(pushGTMAnalytic(payload)),
    setHelpFlag: (payload: boolean) => dispatch(setHelpFlagAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CorporatePage);
